import React, { useEffect, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════
   Mobile layout for the affordability dashboard.

   The components use inline styles, which media queries can't reach, so
   the overrides target them by attribute ([style*="grid-template-columns"])
   inside a `.ddr-root` scope. On phones every multi-column grid collapses
   to one full-width column (grid-12), KPI tiles go two-up, flex rows wrap,
   tables scroll sideways, and on-chart value labels are hidden (the
   tooltip still carries every number) so nothing overlaps.

   Charts that need a different shape on a phone (long category axes)
   read `useIsMobile()` and switch to horizontal bars.
   ══════════════════════════════════════════════════════════════════════ */

export const MOBILE_BP = 768;

export function useIsMobile(bp = MOBILE_BP) {
  const q = `(max-width: ${bp}px)`;
  const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(q).matches;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, [q]);
  return m;
}

const CSS = `
/* Desktop is left completely untouched — every rule is phone-only. */

@media (max-width: ${MOBILE_BP}px) {
  /* every grid → one full-width column */
  .ddr-root [style*="grid-template-columns"] { grid-template-columns: minmax(0, 1fr) !important; }
  .ddr-root [style*="grid-template-columns"] > * { min-width: 0 !important; }
  /* KPI tiles two-up */
  .ddr-root .ddr-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 8px !important; }
  .ddr-root .ddr-tiles > * { padding: 12px !important; }
  .ddr-root .ddr-tiles [style*="font-size: 22px"] { font-size: 18px !important; }

  /* flex rows (toolbars, headers, card titles) wrap instead of overflowing */
  /* rows only — wrapping a column flex box stretches it to its widest child,
     and touching flex-basis inside a card collapses charts to 0px on Safari */
  .ddr-root [style*="display: flex"][style*="gap"]:not([style*="flex-direction: column"]),
  .ddr-root [style*="justify-content: space-between"]:not([style*="flex-direction: column"]) {
    flex-wrap: wrap !important; row-gap: 8px; }
  .ddr-root select { max-width: 100%; }

  /* tighter page padding */
  .ddr-root [style*="padding: 20px"], .ddr-root [style*="padding: 16px"] { padding: 10px !important; }
  .ddr-root [style*="padding: 14px 24px"] { padding: 12px 14px !important; }
  .ddr-root [style*="font-size: 18px"][style*="letter-spacing"] { font-size: 15px !important; }

  /* top tabs: one scrollable row */
  .ddr-root .ddr-tabs { flex-wrap: nowrap !important; overflow-x: auto; padding: 8px 10px 0 !important;
    -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .ddr-root .ddr-tabs > button { flex: 0 0 auto; padding: 8px 12px !important; font-size: 11.5px !important; }

  /* tables scroll sideways inside their card */
  .ddr-root table { display: block; max-width: 100%; overflow-x: auto; white-space: nowrap; }

  /* charts: no on-bar/on-line value labels, smaller ticks and legends */
  .ddr-root .recharts-label-list { display: none; }
  .ddr-root .recharts-cartesian-axis-tick text { font-size: 9px; }
  .ddr-root .recharts-legend-wrapper { font-size: 9px !important; }

  /* map */
  .ddr-root .leaflet-container { height: 380px !important; }
  .ddr-root .ddr-map-card { min-width: 0 !important; max-width: calc(100% - 24px) !important;
    font-size: 10px !important; top: 8px !important; right: 8px !important; }
}
`;

export function ResponsiveStyles() {
  return <style>{CSS}</style>;
}